import React, { useState, useEffect, useRef } from "react";
import Button from "../customComponents/Button";
import Table from "../customComponents/Table";
import DatePicker from "../customComponents/DatePicker";
import { createColumnHelper } from "@tanstack/react-table";
import SearchSelect from "../customComponents/SerachSelect";
import TransportationModal from "../customComponents/TransportationModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import { post } from "../utils/api";
import ConfirmationModal from "../customComponents/ConfirmationModal";
import { ToastContainer, toast } from "react-toastify";
import LoaderComponent from "./LoaderComponent";

const Transportations = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const searchText = searchParams.get("search") || "";
  const cityFilter = searchParams.get("city") || "";
  const vehicleFilter = searchParams.get("vehicletype") || "";

  const updateParams = (updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (v) next.set(k, v);
        else next.delete(k);
      });
      return next;
    });
  };

  const [localSearch, setLocalSearch] = useState(searchText);
  const searchTimerRef = useRef(null);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTransportation, setSelectedTransportation] = useState(null);
  const user = JSON.parse(localStorage.getItem("user"));
  const [tableData, setTableData] = useState([]);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [isDeleteModal, setDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [noOfPages, setNoOfPages] = useState(1);

  useEffect(() => {
    fetchData();
  }, [currentPage, searchText, cityFilter, vehicleFilter]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const data = {
        page: currentPage,
        limit: 10,
        search: searchText.trim(),
        city: cityFilter,
        vehicletype: vehicleFilter,
      };
      const result = await post("/getAllTransport", data);
      setNoOfPages(result?.pagination?.total);

      const temp = result.data.map((list) => ({
        vehicletype: list.vehicaltype,
        vehiclename: list.vehicalname,
        actype: list.actype,
        noofseats: list.noofseats,
        city: list.city,
        alterCreatedon: new Date(list.createdon)
          .toLocaleDateString("en-GB")
          .replace(/\//g, "-"),
        priceperhead: `₹ ${list.priceperhead}`,
        ...list,
      }));
      setTableData(temp);

      if (!optionsLoaded) {
        const citySet = new Set(result.data.map((item) => item.city));
        const vehicleSet = new Set(result.data.map((item) => item.vehicaltype));
        setCityOptions([
          { label: "All City", value: "All City" },
          ...Array.from(citySet).filter(Boolean).map((c) => ({ label: c, value: c })),
        ]);
        setVehicleOptions([
          { label: "All Vehicle", value: "All Vehicle" },
          ...Array.from(vehicleSet).filter(Boolean).map((v) => ({ label: v, value: v })),
        ]);
        setOptionsLoaded(true);
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching transports:", error);
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setLocalSearch(value);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      updateParams({ search: value || null, page: null });
    }, 400);
  };

  const handleFormSelection = (e, type) => {
    if (type === "vehicletype") {
      updateParams({ vehicletype: e.value !== "All Vehicle" ? e.value : null, page: null });
    } else {
      updateParams({ city: e.value !== "All City" ? e.value : null, page: null });
    }
  };

  const handleDateChange = (filterDate) => {
    setSelectedDate(filterDate);
  };

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor("slNo", {
      header: "Sl. No",
      cell: (info) => info.row.index + 1,
    }),
    columnHelper.accessor("vehicletype", {
      header: "TRANSPORTATION TYPE",
    }),
    columnHelper.accessor("vehiclename", {
      header: "TRANSPORTATION NAME",
    }),
    columnHelper.accessor("noofseats", {
      header: "NO. OF SEATS",
    }),
    columnHelper.accessor("city", {
      header: "CITY (SOURCE)",
    }),
    columnHelper.accessor("priceperhead", {
      header: "PRICE PER HEAD",
    }),
    columnHelper.accessor("actions", {
      header: "ACTIONS",
      cell: (info) => (
        <div className="flex gap-2">
          {(user?.role === "Admin" || user?.role === "Backend Team Manager") && (
            <button
              className="p-2 bg-gray-200 rounded-md"
              onClick={() => handleEditClick(info.row.original)}
            >
              <span className="material-symbols-outlined">edit</span>
            </button>
          )}
          <button
            className="p-2 bg-gray-200 rounded-md"
            onClick={() => handleViewClick(info.row.original)}
          >
            <span className="material-symbols-outlined">visibility</span>
          </button>
          {(user?.role === "Admin" || user?.role === "Backend Team Manager") && (
            <button
              onClick={() => handleDeleteClick(info.row.original)}
              className="p-2 bg-gray-200 text-red-500 rounded-md"
            >
              <span className="material-symbols-outlined">delete</span>
            </button>
          )}
        </div>
      ),
    }),
  ];

  const handleEditClick = (transportation) => {
    navigate("/add-transportation", {
      state: { transportData: transportation, isEdit: true },
    });
  };

  const handleViewClick = (transportation) => {
    setSelectedTransportation(transportation);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTransportation(null);
  };

  const handleDeleteClick = (data) => {
    setDeleteModal(true);
    setDeleteItem(data);
  };

  const handleConfirmDelete = async () => {
    try {
      await post("/deleteTransport", deleteItem);
      toast.success("Vehicle deleted successfully");
      setDeleteModal(false);
      fetchData();
    } catch (error) {
      toast.error("Vehicle delete failed");
      setDeleteModal(false);
    }
  };

  const handleCloseModal = () => {
    setDeleteModal(false);
  };

  return (
    <div>
      <div className="flex justify-between">
        <div>
          <h2 className="text-primary-black text-[28px] font-semibold">
            Transportations
          </h2>
          <p className="text-[14px]">
            <span className="font-bold">Transportation</span> /{" "}
            <span className="font-regular">Transportations List</span>
          </p>
        </div>
        <div className="flex mt-4 justify-end gap-4">
          <Button
            type="primary"
            label="Add Vehicle"
            onClick={() => navigate("/add-transportation")}
          />
        </div>
      </div>

      {/* Filter Fields */}
      <div className="grid grid-cols-1 gap-4 mt-4 md:grid-cols-2 lg:flex lg:justify-between lg:items-center lg:gap-4">
        <div className="w-full md:w-full lg:flex-[2]">
          <input
            type="text"
            className="outline-none p-2 rounded-l-lg w-full h-[40px]"
            placeholder="Search..."
            value={localSearch}
            onChange={handleSearch}
          />
        </div>

        <div className="w-full md:w-full lg:flex-1">
          <SearchSelect
            className="w-full"
            placeholder="Transportation Type"
            value={vehicleOptions.find((o) => o.value === vehicleFilter) || null}
            onChange={(e) => handleFormSelection(e, "vehicletype")}
            options={vehicleOptions}
          />
        </div>

        <div className="w-full md:w-full lg:flex-1">
          <SearchSelect
            className="w-full"
            placeholder="City"
            value={cityOptions.find((o) => o.value === cityFilter) || null}
            options={cityOptions}
            onChange={(e) => handleFormSelection(e, "city")}
          />
        </div>

        <div className="w-full md:w-full lg:flex-1">
          <DatePicker
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            placeholderText="Select Date..."
            className="w-full"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border-gray-300 mt-7 pb-6">
        {isLoading ? (
          <LoaderComponent />
        ) : (
          <Table
            columns={columns}
            data={tableData}
            noOfPages={noOfPages}
            currentPage={currentPage}
            setCurrentPage={(page) => updateParams({ page: page > 1 ? String(page) : null })}
          />
        )}
      </div>

      <ConfirmationModal
        isOpen={isDeleteModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        title="Delete Transportation"
        message={`Are you sure you want to delete the Transportation "${deleteItem?.vehiclename}"?`}
      />

      {isModalOpen && selectedTransportation && (
        <TransportationModal
          isOpen={isModalOpen}
          onClose={closeModal}
          transportationData={selectedTransportation}
        />
      )}
      <ToastContainer />
    </div>
  );
};

export default Transportations;
